import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import { useMutation } from "react-query";
import { useParams, useSearchParams } from "react-router-dom";
import usePopup from "../hooks/usePopup";
import { getReviewLink, submitPatientFeedback } from "./services";

type PageView = "choice" | "feedback" | "thank-you";

const PatientFeedbackPage = () => {
  const { clinicId = "", patientId = "" } = useParams<{
    clinicId: string;
    patientId: string;
  }>();
  const [urlSearchParams] = useSearchParams();
  const tenantClinicId = urlSearchParams.get("tenantClinicId") || "";
  const { showSnackbar } = usePopup();
  const [view, setView] = useState<PageView>("choice");
  const [feedback, setFeedback] = useState("");

  const parsedClinicId = useMemo(() => Number(clinicId), [clinicId]);
  const parsedPatientId = useMemo(() => Number(patientId), [patientId]);

  const isValidRoute =
    Boolean(clinicId && patientId && tenantClinicId) &&
    !Number.isNaN(parsedClinicId) &&
    parsedClinicId > 0 &&
    !Number.isNaN(parsedPatientId) &&
    parsedPatientId > 0;

  const { mutate: fetchReviewLink, isLoading: isReviewLinkLoading } =
    useMutation(getReviewLink, {
      onSuccess: (result) => {
        if (result.responseIndicator === "success" && result.response) {
          window.location.assign(result.response);
          return;
        }

        showSnackbar({
          message:
            result.responseMessage ||
            "Google review link is not configured for this clinic.",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
      onError: (error: any) => {
        showSnackbar({
          message:
            error?.response?.data?.responseMessage ||
            "Failed to load review link",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    });

  const { mutate: submitFeedback, isLoading: isSubmitting } = useMutation(
    submitPatientFeedback,
    {
      onSuccess: (result) => {
        if (result.responseIndicator === "success") {
          setView("thank-you");
          setFeedback("");
        } else {
          showSnackbar({
            message: result.responseMessage || "Failed to submit feedback",
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (error: any) => {
        showSnackbar({
          message:
            error?.response?.data?.responseMessage ||
            "Something went wrong. Please try again.",
          open: true,
          variant: "warning",
          duration: 3000,
        });
      },
    },
  );

  const handleLike = () => {
    if (!isValidRoute) {
      showSnackbar({
        message:
          "Invalid feedback link. Clinic or patient details are missing.",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    fetchReviewLink(tenantClinicId);
  };

  const handleDislike = () => {
    setView("feedback");
  };

  const handleSubmitFeedback = () => {
    if (!isValidRoute) {
      showSnackbar({
        message:
          "Invalid feedback link. Clinic or patient details are missing.",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    if (!feedback.trim()) {
      showSnackbar({
        message: "Please enter your feedback before submitting.",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }

    submitFeedback({
      feedBack: feedback.trim(),
      clinicId: parsedClinicId,
      patientId: parsedPatientId,
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "radial-gradient(circle at top, #f5f9ff, #eef2f7)",
        p: 2,
      }}
    >
      <Card
        elevation={12}
        sx={{
          maxWidth: 520,
          width: "100%",
          borderRadius: 4,
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #1e3c72, #2a5298)",
                color: "#fff",
              }}
            >
              <RateReviewOutlinedIcon sx={{ fontSize: 36 }} />
            </Box>

            <Stack spacing={1} alignItems="center">
              <Typography variant="h5" fontWeight={700} textAlign="center">
                How was your visit?
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                Your feedback helps us improve our care.
              </Typography>
            </Stack>

            {isValidRoute && view === "choice" && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                width="100%"
              >
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  color="success"
                  startIcon={
                    isReviewLinkLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <ThumbUpAltOutlinedIcon />
                    )
                  }
                  onClick={handleLike}
                  disabled={isSubmitting || isReviewLinkLoading}
                  sx={{ py: 1.4, borderRadius: 3, fontWeight: 600 }}
                >
                  {isReviewLinkLoading ? "Loading..." : "Like"}
                </Button>
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  color="error"
                  startIcon={<ThumbDownAltOutlinedIcon />}
                  onClick={handleDislike}
                  disabled={isSubmitting || isReviewLinkLoading}
                  sx={{ py: 1.4, borderRadius: 3, fontWeight: 600 }}
                >
                  Dislike
                </Button>
              </Stack>
            )}

            {!isValidRoute && (
              <Typography variant="body2" color="error" textAlign="center">
                This feedback link is invalid. Please use the complete link
                shared with you.
              </Typography>
            )}

            {isValidRoute && view === "feedback" && (
              <Stack spacing={2} width="100%">
                <TextField
                  label="Tell us what we can improve"
                  placeholder="Share your feedback here..."
                  value={feedback}
                  onChange={(event) => setFeedback(event.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                />
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setView("choice")}
                    disabled={isSubmitting}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSubmitFeedback}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send feedback"}
                  </Button>
                </Stack>
              </Stack>
            )}

            {view === "thank-you" && (
              <Stack spacing={2} alignItems="center" width="100%">
                <Typography variant="h6" fontWeight={600} textAlign="center">
                  Thank you for your feedback
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  We appreciate you taking the time to help us improve.
                </Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PatientFeedbackPage;
